import { useQueryClient } from '@tanstack/vue-query';
import { match, P } from 'ts-pattern';
import { nextTick, ref, type Ref } from 'vue';
import { useRouter } from 'vue-router';

import { useKycProcess } from '@/hooks/kyc/use-kyc-process.ts';
import { useAnalytics } from '@/hooks/use-analytics.ts';
import { setSentryUser } from '@/lib/sentry-helpers.ts';
import { type User, UserStatusSchema, UserRoleSchema } from '@/schemas/users/user-schema.ts';
import type { UserRole } from '@/types/users/user-role-enum';
import type { UserStatus } from '@/types/users/user-status-enum';

const currentUser: Ref<User | undefined> = ref();
const isAuthenticated: Ref<boolean> = ref(false);
// @ts-expect-error TODO: bump the typescript-eslint stack in order to bump compilerOptions.lib ES version
const waitForAuthSync: Ref<ReturnType<typeof Promise.withResolvers>> = ref(
  // @ts-expect-error TODO: bump the typescript-eslint stack in order to bump compilerOptions.lib ES version
  Promise.withResolvers(),
);

const isUserRole = (role: UserStatus | UserRole): role is UserRole =>
  UserRoleSchema.safeParse(role).success;

const isUserStatus = (role: UserStatus | UserRole): role is UserStatus =>
  UserStatusSchema.safeParse(role).success;

export function useAuth() {
  const queryClient = useQueryClient();
  const router = useRouter();
  const analytics = useAnalytics();
  const { resetKycData } = useKycProcess();

  const isAuthorized = (role: keyof typeof UserStatus | keyof typeof UserRole) => {
    return match(role)
      .with(P.when(isUserRole), (r) => currentUser.value?.role === r)
      .with(P.when(isUserStatus), (s) => currentUser.value?.status === s)
      .exhaustive();
  };

  const identifyUserOnVendorServices = (user: User) => {
    analytics.identify(user.id, user);
    setSentryUser(user);
    currentUser.value = user;
  };

  const setAuthState = async (state: boolean) => {
    const previousState = isAuthenticated.value;
    isAuthenticated.value = state;
    if (previousState && !state) {
      await nextTick();
      resetKycData();
      await queryClient.resetQueries({ predicate: (query) => query.meta?.authRequired === true });
    }
    waitForAuthSync.value.resolve(state);
  };

  const logoutAction = async () => {
    await setAuthState(false);
    const currentRoute = router.currentRoute.value;

    if (currentRoute.meta.authRequired === true) {
      await router.push({ path: '/' });
    }
    currentUser.value = undefined;
  };

  return {
    isAuthenticated,
    // Promise.withResolver ref to wait after auth state sync between backend and frontend
    waitForAuthSync,
    isAuthorized,
    identifyUserOnVendorServices,
    login: async () => setAuthState(true),
    logout: async () => logoutAction(),
  };
}
